/** Site survey helpers that only need the project data (ordering, signature, assets). */
import type { Id, Project, SurveyBoard, SurveyCircuit, SurveyPoint, SurveyRoom } from './types.ts';

const byOrder = <T extends { order: number; id: string }>(a: T, b: T) =>
  a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const surveyRooms = (p: Project): SurveyRoom[] => Object.values(p.surveyRooms ?? {}).sort(byOrder);
export const surveyBoards = (p: Project): SurveyBoard[] => Object.values(p.surveyBoards ?? {}).sort(byOrder);
export const roomPoints = (p: Project, roomId: Id): SurveyPoint[] =>
  Object.values(p.surveyPoints ?? {})
    .filter((x) => x.roomId === roomId)
    .sort(byOrder);

/**
 * A board's circuits in board order: first those fed by the main bus, then each RCD's in the
 * order of the board's RCDs (the order they are drawn on the single-line diagram).
 */
export function boardCircuits(p: Project, boardId: Id): SurveyCircuit[] {
  const board = p.surveyBoards?.[boardId];
  const all = Object.values(p.surveyCircuits ?? {})
    .filter((c) => c.boardId === boardId)
    .sort(byOrder);
  if (!board) return all;
  const keys = board.rcds.map((r) => r.key);
  const rank = (c: SurveyCircuit) => (c.rcdKey && keys.includes(c.rcdKey) ? keys.indexOf(c.rcdKey) + 1 : 0);
  return all
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map((x) => x.c);
}

/** Points fed by a circuit, in room order then line order (the order on the diagram). */
export function circuitPoints(p: Project, circuitId: Id): SurveyPoint[] {
  const rooms = new Map(surveyRooms(p).map((r, i) => [r.id, i]));
  return Object.values(p.surveyPoints ?? {})
    .filter((x) => x.circuitId === circuitId)
    .sort((a, b) => (rooms.get(a.roomId) ?? 1e9) - (rooms.get(b.roomId) ?? 1e9) || byOrder(a, b));
}

export const hasSurvey = (p: Project) =>
  Object.keys(p.surveyRooms ?? {}).length > 0 || Object.keys(p.surveyBoards ?? {}).length > 0;

/** Next `order` value for a collection (appends at the end). */
export const nextOrder = (items: Record<string, { order: number }> | undefined) =>
  Math.max(-1, ...Object.values(items ?? {}).map((x) => x.order)) + 1;

function fnv(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** JSON with sorted keys, so equal data gives equal text. */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object')
    return `{${Object.keys(v)
      .sort()
      .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${stable((v as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  return JSON.stringify(v);
}

/**
 * Fingerprint of everything in the survey that shapes the generated diagrams (not notes or
 * photos). A generated sheet keeps the signature it was made from; a different value means
 * the survey changed since.
 */
export function surveySignature(p: Project): string {
  const rooms = surveyRooms(p).map(({ notes: _n, photos: _p, ...r }) => r);
  const points = Object.values(p.surveyPoints ?? {})
    .map(({ notes: _n, photos: _p, ...x }) => x)
    .sort((a, b) => (a.id < b.id ? -1 : 1));
  const boards = surveyBoards(p).map(({ notes: _n, photos: _p, earthOhm: _e, ...b }) => b);
  const circuits = Object.values(p.surveyCircuits ?? {})
    .map(({ notes: _n, ...c }) => c)
    .sort((a, b) => (a.id < b.id ? -1 : 1));
  return fnv(stable({ rooms, points, boards, circuits }));
}

export interface AssetRef {
  id: string;
  mime: string;
}

const mimeFromName = (id: string) =>
  /\.png$/i.test(id) ? 'image/png' : /\.webp$/i.test(id) ? 'image/webp' : 'image/jpeg';

/**
 * Every asset the project uses: plan backgrounds, survey photos, photos of points and devices,
 * inspection report pages and photos of fixes.
 */
export function assetRefs(p: Project): AssetRef[] {
  const out = new Map<string, string>();
  for (const s of Object.values(p.sheets)) if (s.background) out.set(s.background.assetId, s.background.mime);
  const add = (ids: readonly string[] | undefined) => {
    for (const id of ids ?? []) if (!out.has(id)) out.set(id, mimeFromName(id));
  };
  for (const r of Object.values(p.surveyRooms ?? {})) add(r.photos);
  for (const b of Object.values(p.surveyBoards ?? {})) add(b.photos);
  for (const x of Object.values(p.surveyPoints ?? {})) add(x.photos);
  for (const e of Object.values(p.elements)) add(e.photos);
  for (const i of Object.values(p.inspections ?? {})) add(i.pages);
  for (const r of Object.values(p.remarks ?? {})) add(r.photos);
  return [...out].map(([id, mime]) => ({ id, mime }));
}
